"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer({
  scrolled
}: {
  scrolled: boolean;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-3 w-16 bg-foreground/10 animate-pulse rounded-md" />
        <div className="h-3 w-16 bg-foreground/10 animate-pulse rounded-md" />
        <div className="h-3 w-16 bg-foreground/10 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6 lg:gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={`text-foreground !bg-transparent hover:!bg-transparent focus:!bg-transparent text-[12px] uppercase tracking-[0.18em] font-default transition-opacity duration-300 ${scrolled ? "opacity-90 hover:opacity-100" : "opacity-85 hover:opacity-100"}`}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 px-8">
        <div className="h-10 w-3/4 bg-foreground/10 animate-pulse rounded-md" />
        <div className="h-10 w-2/3 bg-foreground/10 animate-pulse rounded-md" />
        <div className="h-10 w-1/2 bg-foreground/10 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-8 px-8">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={onClose} data-index={i} className="font-default italic text-foreground text-4xl sm:text-5xl leading-tight hover:opacity-70 transition-opacity duration-300">
          {item.label}
        </Link>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href === "/#") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (hashPart === "") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        if (path === "/" || path === window.location.pathname) {
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="4330" id="header" className={`sticky top-0 z-50 w-full transition-all duration-[600ms] ease-out ${scrolled ? "bg-background/70 backdrop-blur-xl border-b border-border" : "bg-transparent border-b border-transparent"} text-foreground`}>
      <div className={`mx-auto max-w-7xl px-6 lg:px-10 flex items-center justify-between transition-all duration-[600ms] ease-out ${scrolled ? "h-16" : "h-20 md:h-24"}`}>
        <div className="hidden md:flex flex-1 items-center justify-start">
          {!searchOpen ? <MenuProvider menu_id="4">
              <DesktopMenuConsumer scrolled={scrolled} />
            </MenuProvider> : <div className="w-full max-w-md flex items-center gap-3">
              <Search className="h-4 w-4 text-foreground/70" />
              <Input autoFocus placeholder="Search services..." className="border-0 border-b border-foreground/30 bg-transparent px-0 h-8 text-foreground placeholder:text-foreground/40 text-sm tracking-wide focus-visible:ring-0 focus-visible:border-foreground" />
              <Button variant="ghost" size="icon" onClick={() => setSearchOpen(false)} className="h-7 w-7 text-foreground/70 hover:text-foreground hover:bg-transparent" aria-label="Close search">
                <X className="h-4 w-4" />
              </Button>
            </div>}
        </div>

        <div className="md:hidden flex items-center">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 text-foreground hover:bg-transparent hover:text-foreground" aria-label="Open menu">
                <Menu className="h-5 w-5" strokeWidth={1.25} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-full sm:max-w-full bg-background text-foreground border-0 p-0 [&>button]:hidden">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex items-center justify-between h-20 px-6 border-b border-border">
                <Link to="/" onClick={() => setMobileOpen(false)}>
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723817/img/1D69EE56-30DB-4891-9EEC-CB9865D2838D.png" alt="Logo" className="h-7 w-auto" />
                </Link>
                <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} className="h-9 w-9 text-foreground hover:bg-transparent hover:text-foreground" aria-label="Close menu">
                  <X className="h-5 w-5" strokeWidth={1.25} />
                </Button>
              </div>
              <div className="flex-1 flex flex-col justify-center py-12">
                <MenuProvider menu_id="4">
                  <MobileMenuConsumer onClose={() => setMobileOpen(false)} />
                </MenuProvider>
              </div>
              <div className="px-8 pb-12 flex items-center gap-6 text-foreground/60 text-[11px] uppercase tracking-[0.22em]">
                <span>Search</span>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <div className="flex-shrink-0 md:absolute md:left-1/2 md:-translate-x-1/2">
          <Link to="/" aria-label="Price Advocate Services home">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723817/img/1D69EE56-30DB-4891-9EEC-CB9865D2838D.png" alt="Logo" className={`w-auto transition-all duration-[600ms] ease-out ${scrolled ? "h-8" : "h-10 md:h-12"}`} />
          </Link>
        </div>

        <div className="hidden md:flex flex-1 items-center justify-end gap-6 lg:gap-8">
          <MenuProvider menu_id="5">
            <DesktopMenuConsumer scrolled={scrolled} />
          </MenuProvider>

          <span className="h-4 w-px bg-foreground/20" aria-hidden="true" />

          <div className="flex items-center gap-2">
            {!searchOpen && <Button variant="ghost" size="icon" onClick={() => setSearchOpen(true)} className="h-9 w-9 text-foreground hover:bg-transparent hover:text-foreground opacity-85 hover:opacity-100" aria-label="Open search">
                <Search className="h-4 w-4" strokeWidth={1.5} />
              </Button>}
          </div>
        </div>
      </div>
    </section>;
}